"use client";

import { useState, useTransition } from "react";
import {
  addFavourite,
  removeFavourite,
  type FavouriteSpotType,
} from "@/app/actions/favourites";
import { useSpotUser } from "@/app/_components/conditions/SpotUserProvider";
import {
  SavedSpotCallPreferences,
  type SavedSpotCallPreferenceConfig,
} from "./SavedSpotCallPreferences";
import {
  FAVOURITE_SAVE_CONFIRMATION,
  FAVOURITE_BUTTON_TOUCH_TARGET_CLASS,
  favouriteButtonLabel,
  type FavouritePendingAction,
} from "./favourite-button-state";
import { conditionSaveSignupSport } from "./save-to-companion-state";
import {
  buildCompanionSignupHref,
  buildConditionSaveSignupIntentInput,
  writeCompanionSignupIntent,
} from "@/lib/companion/signup-intent";

type Props = {
  spotId: string;
  spotType?: FavouriteSpotType;
  callPreference?: SavedSpotCallPreferenceConfig;
};

export function FavouriteButton({ spotId, spotType = "surf", callPreference }: Props) {
  const { isLoggedIn, isFav, callSports } = useSpotUser();
  // null until the user toggles; the fetched state is the default
  const [override, setOverride] = useState<boolean | null>(null);
  const [message, setMessage] = useState<{ kind: "success" | "error"; text: string } | null>(null);
  const [pendingAction, setPendingAction] = useState<FavouritePendingAction>(null);
  const [isPending, startTransition] = useTransition();

  const favourited = override ?? isFav;
  const label = favouriteButtonLabel({
    isLoggedIn,
    favourited,
    pendingAction: isPending ? pendingAction : null,
  });

  function handleClick() {
    setMessage(null);

    if (!isLoggedIn) {
      const signupIntent = buildConditionSaveSignupIntentInput({
        spotSlug: spotId,
        sport: conditionSaveSignupSport(
          spotType,
          callPreference?.allowedSupProfiles,
        ),
      });
      writeCompanionSignupIntent(signupIntent);
      window.location.href = buildCompanionSignupHref(signupIntent);
      return;
    }

    const next = !favourited;
    setOverride(next);
    setPendingAction(next ? "add" : "remove");

    startTransition(async () => {
      const result = next
        ? await addFavourite(spotId, spotType)
        : await removeFavourite(spotId, spotType);

      if (!result.success) {
        setOverride(!next);
        setMessage({
          kind: "error",
          text: result.error ?? "Something went wrong. Please try again.",
        });
      } else if (next) {
        setMessage({ kind: "success", text: FAVOURITE_SAVE_CONFIRMATION });
      }
      setPendingAction(null);
    });
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={handleClick}
        disabled={isPending}
        className={[
          `inline-flex ${FAVOURITE_BUTTON_TOUCH_TARGET_CLASS} items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12px] font-semibold transition-colors disabled:opacity-60`,
          favourited
            ? "border-red-200 bg-red-50 text-red-600 hover:bg-red-100"
            : "border-white/20 bg-white/10 text-white/90 hover:bg-white/20",
        ].join(" ")}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill={favourited ? "currentColor" : "none"}
          aria-hidden="true"
        >
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
        </svg>
        {label}
      </button>
      {message && !(message.kind === "success" && callPreference && favourited && isLoggedIn) && (
        <p
          role="status"
          className={`absolute left-0 top-full mt-1 max-w-[260px] text-[11px] leading-snug ${
            message.kind === "error" ? "text-red-400" : "text-emerald-100"
          }`}
        >
          {message.text}
        </p>
      )}
      {callPreference && favourited && isLoggedIn && (
        <SavedSpotCallPreferences
          spotId={spotId}
          spotType={spotType}
          spotName={callPreference.spotName}
          category={callPreference.category}
          allowedSupProfiles={callPreference.allowedSupProfiles}
          initialCallSports={callSports}
        />
      )}
    </div>
  );
}
